/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Directive, ElementRef, HostBinding, HostListener, Input, Self } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

@Directive({
  selector: '[detectEllipsis]',
  standalone: true,
})
export class DetectEllipsisDirective {
  // https://ggsystems.atlassian.net/browse/CC-2056
  private isManualToggleNeeded = true;

  private isMultilineNeeded = () => this.multiline !== undefined && this.multiline > 0;

  private getEllipsisStyle = () =>
    this.isMultilineNeeded()
      ? `
    display: -webkit-box;
    -webkit-line-clamp: ${this.multiline};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    `
      : `
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
    `;

  @Input('detectEllipsis')
  tooltipText?: string;

  @Input() multiline?: number;

  // tslint:disable-next-line:no-input-rename
  @Input('matTooltip')
  matTooltipMessage = '';

  @HostBinding('style')
  get styles(): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(this.getEllipsisStyle());
  }

  @HostListener('mouseenter')
  @HostListener('touchstart')
  onMouseEnter() {
    if (this.isManualToggleNeeded && this.matTooltip.message) this.isManualToggleNeeded = false;
    const { scrollWidth, offsetWidth, scrollHeight, offsetHeight, textContent } = this.el.nativeElement;
    const isOverflow = this.isMultilineNeeded() ? scrollHeight > offsetHeight : scrollWidth > offsetWidth;
    let message = this.matTooltipMessage ? this.matTooltipMessage + '\n' : '';
    message += isOverflow ? this.tooltipText || textContent : '';
    this.matTooltip.message = message;
    if (this.isManualToggleNeeded) this.matTooltip.show();
  }

  @HostListener('mouseout')
  @HostListener('touchend')
  onMouseOut() {
    if (this.isManualToggleNeeded) {
      this.matTooltip.hide();
      this.isManualToggleNeeded = false;
    }
  }

  public constructor(
    private el: ElementRef,
    @Self() private matTooltip: MatTooltip,
    private sanitizer: DomSanitizer,
  ) {}
}
